import { AbsgArrayHelper, LogService } from '@absg/angular-common';
import { MSODataQuery, ODataResult } from '@absg/angular-components';
import { DateTimeHelper } from '@absg/angular-platform';
import {
  AutoConfigResponse, AutoConfigureRequest, ClinicChargeApi, ClinicChargeDetail, ClinicChargeODataApi, ClinicChargeReturnDetail, ClinicChargeReturnInfoRequest, DeleteInventoryAssignment,
  DeleteInventoryAssignmentResponse, DispenseApi, DispenseDetailODataApi, DispenseTransactionHistoryRequest, InventoryApi, InventoryAssignments, InventoryItemSummary, InventoryItemUth,
  ManagePositionApi, MedOrderTransactionActivityDetail, PatientDispenseDetail, PatientDispenseDetailSnapshot, PendingInvItem, PositionContainer, ReturnApi, ReturnWasteInfoRequest,
  ReturnWasteLot, ReturnWasteLotInfo, SearchDispenseDetails, TransactionDetailApi, TransactionDetailODataApi, UpdateLotNumberRequest, UpdatePatientOwnedItemTypeRequest,
} from '@absg/api-inventory';
import { UserLite } from '@absg/api-party';
import {
  BaseService, CalculationHelper, ClinicChargeItemViewModel, ClinicChargeReturnDetailModel, ClinicChargeSearchModel, InventoryItem, OdataSearchHelper,
  PartyService, PatientDispenseHistoryFilterOptions, PatientDispenseHistoryQuery, PatientDispenseItemViewModel,
} from '@absg/nim-client-shared';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ReturnWasteTransactionTypes } from 'inventory.root/shared/enums/return-waste-transaction-types.enum';
import { ReturnWasteHistoryViewModel } from 'inventory.root/shared/models/return-waste-history.model';
import { ReturnWasteInfoViewModel } from 'inventory.root/shared/models/return-waste-info.model';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, take } from 'rxjs/operators';
import { StationDetail } from 'shared.root/models/station-details.model';

@Injectable({ providedIn: 'root' })
export class InventoryService extends BaseService {
  constructor(
    private managePositionApi: ManagePositionApi,
    private transactionDetailApi: TransactionDetailApi,
    private transactionDetailODataApi: TransactionDetailODataApi,
    private clinicChargeApi: ClinicChargeApi,
    private clinicChargeODataApi: ClinicChargeODataApi,
    private partyService: PartyService,
    private inventoryApi: InventoryApi,
    private dispenseDetailODataApi: DispenseDetailODataApi,
    private returnApi: ReturnApi,
    private dispenseApi: DispenseApi,
    // private stationInfoService: StationInfoService,
    loggingService: LogService,
  ) {
    super(loggingService);
  }

  autoConfigure(autoConfigRequest: AutoConfigureRequest): Observable<AutoConfigResponse> {
    return this.dispenseApi.autoConfigure(autoConfigRequest)
      .pipe(
        take(1),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      ) as Observable<AutoConfigResponse>;
  }

  getClinicChargeHistory(searchParam: ClinicChargeSearchModel): Observable<ClinicChargeItemViewModel[]> {
    const query = this.getMSODataQuery(searchParam);
    return this.clinicChargeODataApi.getClinicChargePointInTimeHistoryOData({ search: query })
      .pipe(
        concatMap(elem => {
          if (elem.value) {
            const userIds: number[] = AbsgArrayHelper.uniq(AbsgArrayHelper.flatten(AbsgArrayHelper.map(elem.value,
              info => [info.createdById]))).filter((a) => !!a && a > 0);

            if (userIds.length > 0) {
              return this.partyService.getUserBasicInformationList(userIds)
                .pipe(
                  take(1),
                  map(users => this.getClinicChargeViewModels(elem.value, users),
                  ),
                );
            }

            return of(this.getClinicChargeViewModels(elem.value));
          } else {
            return null;
          }
        }),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      ) as Observable<ClinicChargeItemViewModel[]>;
  }

  getClinicChargeReturn(supplierProductInclusionId: number, transactionId: number): Observable<ClinicChargeReturnDetailModel[]> {
    const request: ClinicChargeReturnInfoRequest = {
      clinicChargeTransactionId: transactionId,
      supplierProductInclusionId,
    };

    return this.clinicChargeApi.getClinicChargeReturn(request).pipe(
      concatMap((results) => {
        if (results) {
          const userIds: number[] = AbsgArrayHelper.uniq(AbsgArrayHelper.flatten(AbsgArrayHelper.map(results,
            info => [info.clinicChargeReturnTransactionUserId]))).filter((a => !!a && a > 0));

          if (userIds.length > 0) {
            return this.partyService.getUserBasicInformationList(userIds)
              .pipe(
                take(1),
                map(users => this.getClinicChargeReturnDetailModels(results, users)),
              );
          } else {
            return of(this.getClinicChargeReturnDetailModels(results));
          }
        } else {
          return of([]);
        }
      }),
      catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
    );
  }

  getInventoryItemsByStationId(): Observable<InventoryItemSummary[]> {
    return this.managePositionApi.getStationAssignedItemsV2()
      .pipe(
        map(response => {
          // TODO: Need to address the paging through the results, as the current implementation only grabs the first page.
          const responseData = response as InventoryItemSummary[];
          return responseData;
        }),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      ) as Observable<InventoryItemSummary[]>;
  }

  getInventoryItemsByPracticeId(): Observable<InventoryItemSummary[]> {
    return this.managePositionApi.getAssignedPracticeItems()
      .pipe(
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      ) as Observable<InventoryItemSummary[]>;
  }

  getPendingQuantityUnassignedItemsByStationId(): Observable<InventoryItemSummary[]> {
    const unassignedItems = new Array<InventoryItemSummary>();
    return of(unassignedItems);
  }

  getItemsWithPendingQuantity(): Observable<PendingInvItem[]> {
    return this.managePositionApi.getItemsWithPendingQuantity()
      .pipe(
        map(response => {
          const responseData = response as PendingInvItem[];
          return responseData;
        }),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      ) as Observable<PendingInvItem[]>;
  }

  getPracticeUnderhoodStations(supplierProductInclusionId: number): Observable<InventoryItemUth[]> {
    return this.inventoryApi.getAssignedPracticeUnderhoodItems(supplierProductInclusionId)
      .pipe(
        map(response => {
          const responseData = response;
          return responseData;
        }),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  saveExpirationDate(invItemProductIdentifierId: number, expirationDate: Date): Observable<boolean> {
    if (expirationDate) {
      return this.managePositionApi.updatePositionExpirationDate(invItemProductIdentifierId, expirationDate);
    } else {
      return this.managePositionApi.removeExpirationDate(invItemProductIdentifierId);
    }
  }

  saveLotNumber(invItemProductIdentifierId: number, lotNumber: string): Observable<boolean> {
    const updateLotNumberRequest = {
      invItemProductIdentifierId,
      lotNumber,
    } as UpdateLotNumberRequest;
    return this.managePositionApi.updateLotNumber(updateLotNumberRequest);
  }

  updatePatientOwnedItemType(inventoryItemId: number, patientOwnedItemTypeId: number, patientOwnedItemTypeName: string, patientOwnedItemTypeShortName: string): Observable<boolean> {
    const updatePatientOwnedItemTypeRequest = {
      inventoryItemId,
      patientOwnedItemTypeId,
      patientOwnedItemTypeName,
      patientOwnedItemTypeShortName,
    } as UpdatePatientOwnedItemTypeRequest;

    return this.managePositionApi.updatePositionPatientOwnedItemType(updatePatientOwnedItemTypeRequest)
      .pipe(take(1), catchError(response => this.handleErrorResponse(response))) as Observable<boolean>;
  }

  updateSupplierProductInclusion(currentInclusionId: number, newInclusionId: number): Observable<boolean> {
    return this.inventoryApi.updateSupplierProductInclusionId(currentInclusionId, newInclusionId)
      .pipe(catchError((error: HttpErrorResponse) => this.handleErrorResponse(error))) as Observable<boolean>;
  }

  deleteAssignment(item: DeleteInventoryAssignment): Observable<DeleteInventoryAssignmentResponse> {
    item.unassignFromStation = true;
    return this.inventoryApi.deleteInventoryAssignmentV2(item)
      .pipe(catchError((error: HttpErrorResponse) => this.handleErrorResponse(error))) as Observable<DeleteInventoryAssignmentResponse>;
  }

  sortInventoryItems(items: InventoryItem[]): InventoryItem[] {
    return items.sort((i1, i2) => i1.name < i2.name ? -1 : 1);
  }

  calculateReturnableAmount(patientDispenseDetail: PatientDispenseDetail): number {
    const returnableAmount = patientDispenseDetail.totalDispenseQty + patientDispenseDetail.wasteQtyAtDispense
      - patientDispenseDetail.returnQtyAfterDispense;
    return returnableAmount;
  }

  calculateWastableAmount(patientDispenseDetail: PatientDispenseDetail): number {
    const wastableAmount = (patientDispenseDetail.totalDispenseQty - patientDispenseDetail.returnQtyAfterDispense
      - patientDispenseDetail.wasteQtyAfterDispense);
    return wastableAmount;
  }

  getSearchDetails(patientId: number, serviceDate: string, transactionDate: string): PatientDispenseHistoryFilterOptions {
    const search: PatientDispenseHistoryFilterOptions = {
      patientId,
    };

    if (serviceDate) {
      search.serviceDate = serviceDate;
    }
    if (transactionDate) {
      search.transactionDate = transactionDate;
    }
    return search;
  }

  getPatientDispenseDetails(searchDispense: SearchDispenseDetails): Observable<PatientDispenseItemViewModel[]> {
    return this.transactionDetailApi.getPatientDispenseDetails(searchDispense)
      .pipe(
        switchMap((dispenseDetails: PatientDispenseDetail[]) => {
          const dispenses: PatientDispenseItemViewModel[] = [];
          if (Array.isArray(dispenseDetails)) {
            dispenseDetails.forEach((detail: PatientDispenseDetail) => {
              if (detail) {
                dispenses.push(this.getPatientDispenseViewModel(detail));
              }
            });
          }

          if (dispenses.length === 0) {
            return of(dispenses);
          }

          return this.getDispenseItemDetailsWithUserNames(dispenses);
        }),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      ) as Observable<PatientDispenseItemViewModel[]>;
  }

  getPatientDispenseDetailsWithSnapshot(filterOptions: PatientDispenseHistoryFilterOptions):
    Observable<PatientDispenseItemViewModel[]> {
    const query = new PatientDispenseHistoryQuery({
      searchFields: OdataSearchHelper.createMSODataSearchFields(['displayName', 'alternateName', 'unitNdc', 'itemId', 'altJCode', 'jCode']),
      pageNumber: 1,
      itemsPerPage: 100,
      orderBy: 'serviceDate desc,transactionDate desc',
      searchTerm: filterOptions.productDetails,
    }
      , filterOptions);
    return this.dispenseDetailODataApi.getPatientDispenseDetailsSnapshotOData({ search: query.buildQuery() })
      .pipe(map(result => {
        const results = result as ODataResult<PatientDispenseDetailSnapshot>;
        const dispenseDetails = results.value as PatientDispenseDetailSnapshot[];
        const dispenses: PatientDispenseItemViewModel[] = [];
        if (Array.isArray(dispenseDetails)) {
          dispenseDetails.map((detail: PatientDispenseDetail) => {
            if (detail) {
              dispenses.push(this.getPatientDispenseViewModelWithSnapshot(detail));
            }
          });
        }
        if (dispenses.length === 0) {
          return dispenses;
        }
        return dispenses;
      }), catchError((response: HttpErrorResponse) => this.handleErrorResponse(response))) as Observable<PatientDispenseItemViewModel[]>;
  }

  getReturnWasteHistoryWithUserNames(returnWasteInfoRequest: ReturnWasteInfoRequest): Observable<ReturnWasteHistoryViewModel> {
    return this.returnApi.getReturnWasteLotInfo(returnWasteInfoRequest)
      .pipe(
        switchMap((returnWasteLotInfos: ReturnWasteLotInfo[]) => {
          if (returnWasteLotInfos) {
            const userIds: number[] = AbsgArrayHelper.uniq(AbsgArrayHelper.flatten(AbsgArrayHelper.map(returnWasteLotInfos,
              info => [info.peerTxnUserId, info.dispenseTransactionUserId])));

            if (userIds.length > 0) {
              return this.partyService.getUserBasicInformationList(userIds)
                .pipe(map(users => this.getHistoryViewModels(returnWasteLotInfos, users)));
            } else {
              return of(this.getHistoryViewModels(returnWasteLotInfos, null));
            }
          } else {
            return of(null);
          }
        }),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  getUnknownLotInfoFromReturnWasteHistory(returnWasteInfoRequest: ReturnWasteInfoRequest): Observable<number> {
    const unknown = 'Unknown';
    let unKnownAmount = 0;
    return this.returnApi.getReturnWasteLotInfo(returnWasteInfoRequest).
      pipe(map((lots: ReturnWasteLotInfo[]) => {
        if (!AbsgArrayHelper.isNil(lots) && lots.length > 0) {
          const unknownLotIndex = lots.findIndex(x => x.lotNumber === unknown);
          if (unknownLotIndex > -1) {
            unKnownAmount = lots.filter(l => l.lotNumber === unknown).reduce((a, b) =>
              CalculationHelper.add(a, (b.amountInDispenseUnit || 0)), 0);
          }
        }
        return unKnownAmount;
      }),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  getDispenseItemDetailsWithUserNames(detailViewModels: PatientDispenseItemViewModel[]): Observable<PatientDispenseItemViewModel[]> {
    const userIds = detailViewModels.map(viewModel => viewModel.dispensedById);
    const uniqueUserIds = userIds.filter((id, index) => userIds.indexOf(id) === index);

    return this.partyService.getUserBasicInformationList(uniqueUserIds)
      .pipe(
        map(users => {
          this.setUserNames(users, detailViewModels);
          return detailViewModels;
        }),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      ) as Observable<PatientDispenseItemViewModel[]>;
  }

  getLotInfoByTransactions(transactionIds: number[]): Observable<ReturnWasteLot[]> {
    return this.transactionDetailApi.getDispenseLotInfo(transactionIds);
  }

  productsAssigned(inclusionIds: number[]): Observable<InventoryAssignments[]> {
    return this.inventoryApi.getStationsWithProductsAssigned(inclusionIds)
      .pipe(catchError((error: HttpErrorResponse) => { this.handleErrorResponse(error); return of([]); }));
  }

  getTransactionDetails(inclusionIds: number[]): Observable<InventoryItemSummary[]> {
    if (inclusionIds.length === 0) {
      return of([]);
    }
    return this.transactionDetailODataApi.getTransactionHistoryDetailsOData({ supplierProductInclusionIds: inclusionIds } as DispenseTransactionHistoryRequest)
      .pipe(
        map(response => response.value as InventoryItemSummary[]),
        catchError((error: HttpErrorResponse) => { this.handleErrorResponse(error); return of([]); }));
  }

  filterData(items: StationDetail[], query: string): StationDetail[] {
    return items.filter((item: StationDetail) => {
      // return all station if no search term is entered
      if (AbsgArrayHelper.isNil(query)) {
        return true;
      }

      const searchTerms = query.replace('-', '/').replace('-', '/');

      // check if any of the search terms match the searchable fields
      let isFound = false;
      isFound = isFound || (searchTerms.length > 0 && !AbsgArrayHelper.isNil(item.stationName) && (
        item.stationName.toLowerCase().startsWith(searchTerms.toLowerCase())));

      return isFound;
    });
  }

  getInventoryItemsLiteByStationId(stationId: number): Observable<InventoryItemSummary[]> {
    return this.managePositionApi.getStationAssignedItemsLite(stationId)
      .pipe(
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      ) as Observable<InventoryItemSummary[]>;
  }

  getDispenseInfoByMedOrderItems(medOrderDetailIdList: number[]): Observable<MedOrderTransactionActivityDetail[]> {
    if (!medOrderDetailIdList || medOrderDetailIdList.length === 0) {
      return of([]);
    }
    return this.dispenseApi.getDispenseInfoByMedOrderItems(medOrderDetailIdList)
      .pipe(
        catchError((error: HttpErrorResponse) => { this.handleErrorResponse(error); return of([]); }));
  }

  sendRefreshInventoryCacheRequestAsync(positionContainerId: number): Observable<boolean> {
    const positionContainer: PositionContainer = {
      positionContainerId,
    };
    return this.inventoryApi.syncStationLotTrackingItems(positionContainer).pipe(take(1));
  }

  private getMSODataQuery(searchParam: ClinicChargeSearchModel): string {
    const queryOption = new MSODataQuery({
      searchFields: OdataSearchHelper.createMSODataSearchFields(['DisplayName', 'AlternateName', 'UnitNdc', 'ItemId']),
      pageNumber: 1,
      itemsPerPage: 50,
      orderBy: 'transactionDate desc',
      searchTerm: searchParam.productDetails,
      excludeCount: true,
    });

    if (searchParam.transactionDate) {
      queryOption.options.andSearch = MSODataQuery.getDateSearchWithDateString(searchParam.transactionDate, 'TransactionDate');

    }
    const query = queryOption.toString();
    return query;
  }

  private setUserNames(users: UserLite[], detailViewModels: PatientDispenseItemViewModel[]): void {
    users.filter(user => user !== null).forEach((user: UserLite) => {
      detailViewModels.filter(viewModel => viewModel.dispensedById === user.userId)
         
        .forEach((_item, index, array) => {
          array[index].dispensedByFirstName = user.firstName;
          array[index].dispensedByLastName = user.lastName;
        });
    });
  }

  private getHistoryViewModels(returnWasteLotInfos: ReturnWasteLotInfo[], users: UserLite[]): ReturnWasteHistoryViewModel {
    const model: ReturnWasteHistoryViewModel = new ReturnWasteHistoryViewModel();
    model.returnWasteInfos = [];
    AbsgArrayHelper.each(returnWasteLotInfos, returnWasteInfo => {
      // is this return or waste
      if (returnWasteInfo && (returnWasteInfo.typeId === ReturnWasteTransactionTypes.Return
        || returnWasteInfo.typeId === ReturnWasteTransactionTypes.Waste)) {
        if (!model.dispenseUserName && users) {
          const dispenseUser = AbsgArrayHelper.first(AbsgArrayHelper.filter(users, (user) =>
            user && returnWasteInfo.dispenseTransactionUserId === user.userId));
          if (dispenseUser) {
            model.dispenseUserName = `${dispenseUser.lastName}, ${dispenseUser.firstName}`;
          }
        }

        if (!returnWasteInfo.isWasteAtDispense && returnWasteInfo.amountInDispenseUnit > 0) {
          const returnWasteHistoryRecord: ReturnWasteInfoViewModel = {
            serviceDate: DateTimeHelper.convertUTCToLocalJSDate(returnWasteInfo.peerTxnServiceDate),
            transactionDate: DateTimeHelper.convertUTCToLocalJSDate(returnWasteInfo.peerTxnDate),
            transactionType: ReturnWasteTransactionTypes[returnWasteInfo.typeId],
            amount: returnWasteInfo.amountInDispenseUnit,
            lotNumber: returnWasteInfo.lotNumber,
          } as ReturnWasteInfoViewModel;

          if (users && returnWasteInfo.peerTxnUserId) {
            const transactionUser = AbsgArrayHelper.first(AbsgArrayHelper.filter(users, (user) => user && returnWasteInfo.peerTxnUserId === user.userId));
            if (transactionUser) {
              returnWasteHistoryRecord.userName = `${transactionUser.lastName}, ${transactionUser.firstName}`;
            }
          }

          model.returnWasteInfos.push(returnWasteHistoryRecord);
        }
      }
    });
    return model;
  }

  private getPatientDispenseViewModelWithSnapshot(detail: PatientDispenseDetailSnapshot): PatientDispenseItemViewModel {
    const dispenseDetail: PatientDispenseItemViewModel = new PatientDispenseItemViewModel();

    Object.assign(dispenseDetail, {
      displayName: detail.displayName,
      alternateName: detail.alternateName,
      isPracticeFavorite: detail.isPracticeFavorite,
      isRefrigerated: detail.isRefrigerated,
      supplierProductInclusionId: detail.supplierProductInclusionId,
      productFormularyType: detail.formularyTypeId,
      patientId: detail.patientId,
      idNumber: detail.idNumber,
      lastName: detail.patientLastName,
      firstName: detail.patientFirstName,
      dispensedByUserId: detail.dispensedByUserId,
      dispensedByUserName: detail.dispensedByUserName,
      dispensedByFirstName: detail.dispensedByFirstName,
      dispensedByLastName: detail.dispensedByLastName,
      patientDateOfBirth: detail.birthDate,
      attendingPhysicianId: detail.attendingPhysicianPartyId,
      serviceDateTime: this.formatDate(detail.serviceDate),
      serviceDate: this.formatDateOnly(detail.serviceDate),
      transactionDate: this.formatDate(detail.transactionDate),
      transactionId: detail.transactionId,
      dispensedById: detail.createdById,
      totalDispenseQty: detail.totalDispenseQty,
      fromCabinetQty: detail.fromCabinetQty,
      fromUnderHoodQty: detail.fromUnderHoodQty,
      toCabinetQty: detail.toCabinetQty,
      toUnderHoodQty: detail.toUnderHoodQty,
      outboundQty: detail.outboundQty,
      returnQtyAtDispense: detail.returnQtyAtDispense,
      returnQtyAfterDispense: detail.returnQtyAfterDispense,
      wasteQtyAtDispense: detail.wasteQtyAtDispense,
      wasteQtyAfterDispense: detail.wasteQtyAfterDispense,
      hasWasteOrAfterDispenseReturn: detail.hasWasteOrAfterDispenseReturn,
      isDiscontinued: detail.isDiscontinued,
      formularyTypeId: detail.formularyTypeId,
      volumeUnitOfMeasureDisplay: detail.volumeUnitOfMeasureDisplay,
      dispenseUnitOfMeasureDisplay: detail.dispenseUnitOfMeasureDisplay,
      purchaseUnitOfMeasureDisplay: detail.purchaseUnitOfMeasureDisplay,
      inventoryUnitOfMeasureDisplay: detail.inventoryUnitOfMeasureDisplay,
      volumeUnitOfMeasureConversionFactor: detail.volumeUnitOfMeasureConversionFactor,
      inventoryUnitOfMeasureConversionFactor: detail.inventoryUnitOfMeasureConversionFactor,
      dispenseUnitOfMeasureConversionFactor: detail.dispenseUnitOfMeasureConversionFactor,
      purchaseUnitOfMeasureConversionFactor: detail.purchaseUnitOfMeasureConversionFactor,
      dispenseTypeId: detail.dispenseTypeId,
      dispenseTypeName: detail.dispenseTypeName,
      ndc: detail.unitNdc,
      returnableAmount: this.calculateReturnableAmount(detail),
      wastableAmount: this.calculateWastableAmount(detail),
      isSelected: false,
      itemId: detail.itemId,
      isWitnessRequired: detail.isWitnessRequired,
      patientOwnedPatientId: detail.patientOwnedPatientId,
      patientOwnedPatientIdNumber: detail.patientOwnedPatientIdNumber,
      patientOwnedPatientFirstName: detail.patientOwnedPatientFirstName,
      patientOwnedPatientLastName: detail.patientOwnedPatientLastName,
      patientOwnedPatientDOB: detail.patientOwnedPatientDOB,
      patientOwnedItemTypeId: detail.patientOwnedItemTypeId,
      patientOwnedItemTypeLongName: detail.patientOwnedItemTypeLongName,
      patientOwnedItemTypeShortName: detail.patientOwnedItemTypeShortName,
      isItem505b2: detail.isItem505b2,
      jCode: detail.jcode,
      altJCode: detail.altJcode,
    } as PatientDispenseItemViewModel);
    return dispenseDetail;
  }

  private getPatientDispenseViewModel(detail: PatientDispenseDetail): PatientDispenseItemViewModel {
    const dispenseDetail: PatientDispenseItemViewModel = new PatientDispenseItemViewModel();

    Object.assign(dispenseDetail, {
      displayName: detail.displayName,
      alternateName: detail.alternateName,
      isPracticeFavorite: detail.isPracticeFavorite,
      isRefrigerated: detail.isRefrigerated,
      supplierProductInclusionId: detail.supplierProductInclusionId,
      patientId: detail.patientId,
      idNumber: detail.idNumber,
      lastName: detail.lastName,
      firstName: detail.firstName,
      patientDateOfBirth: detail.birthDate,
      attendingPhysicianId: detail.attendingPhysicianPartyId,
      serviceDateTime: this.convertUTCtoLocal(detail.serviceDate),
      serviceDate: this.convertUTCtoLocal(detail.serviceDate),
      transactionDate: this.convertUTCtoLocal(detail.transactionDate),
      transactionId: detail.transactionId,
      dispensedById: detail.createdById,
      totalDispenseQty: detail.totalDispenseQty,
      fromCabinetQty: detail.fromCabinetQty,
      fromUnderHoodQty: detail.fromUnderHoodQty,
      toCabinetQty: detail.toCabinetQty,
      toUnderHoodQty: detail.toUnderHoodQty,
      outboundQty: detail.outboundQty,
      returnQtyAtDispense: detail.returnQtyAtDispense,
      returnQtyAfterDispense: detail.returnQtyAfterDispense,
      wasteQtyAtDispense: detail.wasteQtyAtDispense,
      wasteQtyAfterDispense: detail.wasteQtyAfterDispense,
      hasWasteOrAfterDispenseReturn: detail.hasWasteOrAfterDispenseReturn,
      isDiscontinued: detail.isDiscontinued,
      formularyTypeId: detail.formularyTypeId,
      volumeUnitOfMeasureDisplay: detail.volumeUnitOfMeasureDisplay,
      dispenseUnitOfMeasureDisplay: detail.dispenseUnitOfMeasureDisplay,
      purchaseUnitOfMeasureDisplay: detail.purchaseUnitOfMeasureDisplay,
      inventoryUnitOfMeasureDisplay: detail.inventoryUnitOfMeasureDisplay,
      volumeUnitOfMeasureConversionFactor: detail.volumeUnitOfMeasureConversionFactor,
      inventoryUnitOfMeasureConversionFactor: detail.inventoryUnitOfMeasureConversionFactor,
      dispenseUnitOfMeasureConversionFactor: detail.dispenseUnitOfMeasureConversionFactor,
      purchaseUnitOfMeasureConversionFactor: detail.purchaseUnitOfMeasureConversionFactor,
      dispenseTypeId: detail.dispenseTypeId,
      dispenseTypeName: detail.dispenseTypeName,
      ndc: detail.unitNdc,
      returnableAmount: this.calculateReturnableAmount(detail),
      wastableAmount: this.calculateWastableAmount(detail),
      isSelected: false,
      itemId: detail.itemId,
    } as PatientDispenseItemViewModel);
    return dispenseDetail;
  }

  private getClinicChargeViewModels(details: ClinicChargeDetail[], users: UserLite[] = null): ClinicChargeItemViewModel[] {
    if (details.length > 0) {
      return details.map(detail => {
        const vm = this.getClinicChargeViewModel(detail);
        if (users && vm.createdById && vm.createdById > 0) {
          const user = AbsgArrayHelper.first(AbsgArrayHelper.filter(users, (a) => a && vm.createdById === a.userId));
          if (user) {
            vm.userName = `${user.lastName}, ${user.firstName}`;
          }
        }
        return vm;
      });
    } else {
      return [] as ClinicChargeItemViewModel[];
    }
  }

  private getClinicChargeViewModel(detail: ClinicChargeDetail): ClinicChargeItemViewModel {
    const clinicChargeDetail: ClinicChargeItemViewModel = new ClinicChargeItemViewModel();

    Object.assign(clinicChargeDetail, {
      transactionId: detail.transactionId,
      itemId: detail.itemId,
      displayName: detail.displayName,
      alternateName: detail.alternateName,
      isPracticeFavorite: detail.isPracticeFavorite,
      isRefrigerated: detail.isRefrigerated,
      isDiscontinued: detail.isDiscontinued,
      supplierProductInclusionId: detail.supplierProductInclusionId,
      isItem505b2: detail.isItem505b2,
      unitNdc: detail.unitNdc,
      dispenseTypeId: detail.dispenseTypeId,
      dispenseTypeName: detail.dispenseTypeName,
      transactionDate: DateTimeHelper.convertUTCToLocalJSDate(detail.transactionDate),
      volumeUnitOfMeasureDisplay: detail.volumeUnitOfMeasureDisplay,
      dispenseUnitOfMeasureDisplay: detail.dispenseUnitOfMeasureDisplay,
      purchaseUnitOfMeasureDisplay: detail.purchaseUnitOfMeasureDisplay,
      inventoryUnitOfMeasureDisplay: detail.inventoryUnitOfMeasureDisplay,
      volumeUnitOfMeasureConversionFactor: detail.volumeUnitOfMeasureConversionFactor,
      inventoryUnitOfMeasureConversionFactor: detail.inventoryUnitOfMeasureConversionFactor,
      dispenseUnitOfMeasureConversionFactor: detail.dispenseUnitOfMeasureConversionFactor,
      purchaseUnitOfMeasureConversionFactor: detail.purchaseUnitOfMeasureConversionFactor,
      totalChargeQty: detail.totalChargeQty,
      isWitnessRequired: detail.isWitnessRequired,
      formularyTypeId: detail.formularyTypeId,
      returnQtyAfterCharge: detail.returnQtyAfterCharge,
      createdById: detail.createdById,
    } as ClinicChargeItemViewModel);

    return clinicChargeDetail;
  }

  private getClinicChargeReturnDetailModels(
    details: ClinicChargeReturnDetail[],
    users: UserLite[] = null,
  ): ClinicChargeReturnDetailModel[] {
    if (details.length > 0) {
      return details.map(detail => {
        const vm = this.getClinicChargeReturnDetailModel(detail);
        if (users && vm.clinicChargeReturnTransactionUserId && vm.clinicChargeReturnTransactionUserId > 0) {
          const user = AbsgArrayHelper.first(AbsgArrayHelper.filter(users, (a) => a && vm.clinicChargeReturnTransactionUserId === a.userId));
          if (user) {
            vm.userName = `${user.lastName}, ${user.firstName}`;
          }
        }
        return vm;
      });
    } else {
      return [];
    }
  }

  private getClinicChargeReturnDetailModel(detail: ClinicChargeReturnDetail): ClinicChargeReturnDetailModel {
    const returnDetailModel = new ClinicChargeReturnDetailModel();

    Object.assign(returnDetailModel, {
      clinicChargeReturnTransactionId: detail.clinicChargeReturnTransactonId,
      clinicChargeReturnTransactionUserId: detail.clinicChargeReturnTransactionUserId,
      clinicChargeReturnTransactionDate: DateTimeHelper.convertUTCToLocalJSDate(detail.clinicChargeReturnTransactionDate),
      supplierProductInclusionId: detail.supplierProductInclusionId,
      typeId: detail.typeId,
      peerTxnDate: detail.peerTxnDate,
      peerTxnServiceDate: detail.peerTxnServiceDate,
      peerTxnUserId: detail.peerTxnUserId,
      amountInDispenseUnit: detail.amountInDispenseUnit,
      lotNumber: detail.lotNumber,
    });

    return returnDetailModel;
  }

  private convertUTCtoLocal(objDate: Date): Date {
    const dt = DateTimeHelper.convertUTCToLocalJSDate(objDate);
    return dt;
  }

  private formatDateOnly(objDate: Date): Date {
    const dt = new Date(DateTimeHelper.formatDate(DateTimeHelper.convertUTCToLocalJSDate(objDate)));
    return dt;
  }

  private formatDate(objDate: Date): Date {
    const dt = new Date(DateTimeHelper.formatDateTime(DateTimeHelper.convertUTCToLocalJSDate(objDate)));
    return dt;
  }
}
